<script setup>
  import {computed, inject, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentSearchWrapper from "@components/Autocomplete/SearchWrapper.vue";
  import {usePage} from "@inertiajs/vue3";
  import RecordComponentMinCSearch from "@pages/Records/Components/MinCSearch.vue";
  import DiseasesTable from "@pages/Records/Components/Sections/DiseaseBar/Components/Table.vue";
  import {addDisease} from "@pages/Records/Components/Sections/DiseaseBar/Utils/diseaseHelper.js";
  import {MedButton} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "RecordComponentSectionDiseaseBar",
  });

  defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    changeIsEditable: {
      type: Function,
      required: true,
    },
    recordData: {
      type: Object,
      required: true,
    },
    problems: {
      type: Object,
      required: true,
    },
  });

  const page = usePage();

  const getIsMassUpdateProcessing = inject("getIsMassUpdateProcessing");

  const refSearch = ref(null);

  const mode = ref(null);

  const isMassUpdateProcessing = computed(() => getIsMassUpdateProcessing("disease"));

  const addDiseaseToRecord = (disease) => {
    addDisease(page.props.patient.id, page.props.record.id, disease);
  };

  const toggleMode = (newMode) => {
    mode.value = mode.value === newMode ? null : newMode;
  };
</script>

<template>
  <div class="flex items-center space-x-2 px-2 pb-4">
    <h5 class="text-2xl">Erkrankungen / Allergien / Lebensumstände</h5>

    <template v-if="!isEditable">
      <MedButton
        variant="primary"
        class="ml-auto"
        :data-test="getTestId('bar-set-editable-button')"
        @click="changeIsEditable"
      >
        Bearbeiten
      </MedButton>
    </template>

    <template v-if="(isEditable && recordData.diseases?.length >= 2) || mode === 'multi-select'">
      <MedButton
        class="ml-auto"
        :data-test="getTestId('toggle-multi-select-button')"
        @click="toggleMode('multi-select')"
      >
        <template v-if="mode === 'multi-select'">Abbrechen</template>
        <template v-else>Mehrfachauswahl</template>
      </MedButton>
    </template>
  </div>

  <!-- Alert for not loadable diseases -->
  <ComponentAlert
    v-if="problems.unloadableDiseases?.length > 0 || problems.unloadableDiseasesSolved?.length > 0"
    type="warning"
    :test-id="getTestId('bar')"
  >
    <h4 class="font-bold">
      In diesem Bereich befinden sich Erkrankungen, die in der aktualisierten Datenbank ersetzt wurden.
    </h4>

    <p class="mt-2">
      Bitte tauschen Sie folgende Erkrankungen / Allergien / Lebensumstände durch geeignete Neue aus, damit sie im Check
      erkannt und berücksichtigt werden:
    </p>

    <ul class="mt-2 ml-8 list-disc" :data-test="getTestId('bar-unloadable')">
      <template v-if="problems.unloadableDiseases?.length > 0">
        <li v-for="(item, index) in problems.unloadableDiseases" :key="'unsolved-' + index">
          <em>{{ item.disease }}</em>
        </li>
      </template>

      <template v-if="problems.unloadableDiseasesSolved?.length > 0">
        <!-- prettier-ignore -->
        <li v-for="(item, index) in problems.unloadableDiseasesSolved" :key="'solved-' + index">
          <em>{{ item.old.disease }}</em>
          (kann ggf. ersetzt werden durch <em>{{ item.new.Name }}</em>)
        </li>
      </template>
    </ul>
  </ComponentAlert>

  <!-- Alert for contradictory warnings (circumstances or gender mismatch) -->
  <ComponentAlert
    v-if="problems.contradictions && problems.contradictions.length > 0"
    type="warning"
    :test-id="getTestId('bar-circumstance')"
  >
    <h4 class="font-bold">Es wurden widersprüchliche Angaben gemacht:</h4>

    <ul class="ml-8 list-disc">
      <li v-for="(contradiction, index) in problems.contradictions" :key="index" v-text="contradiction" />
    </ul>
  </ComponentAlert>

  <!-- Table -->
  <DiseasesTable :record-data="recordData" :is-editable="isEditable" :test-id="getTestId('bar')" :mode="mode" />

  <!-- MinC Search -->
  <ComponentSearchWrapper
    v-if="isEditable"
    :set-focus="() => $refs.refSearch.focus()"
    :disabled="isMassUpdateProcessing"
    class="my-4"
    :test-id="getTestId('bar')"
  >
    <RecordComponentMinCSearch
      ref="refSearch"
      label="Erkrankung / ICD-10 / Allergie / Lebensumstand"
      placeholder="Suchen"
      class="ml-1 w-96"
      filter="diseases"
      :reset-on-select="true"
      :filter-list="true"
      :disabled="isMassUpdateProcessing"
      :test-id="getTestId('bar')"
      @input="addDiseaseToRecord"
    />
  </ComponentSearchWrapper>
</template>
